import {Component, OnInit} from "@angular/core";
import {AlertController, ModalController, NavController} from 'ionic-angular';
import {Observable} from 'rxjs/Rx';

import {MeasureContextService} from '../../../app/core/measure-context.service';
import {MeasureInputService} from '../../../app/core/measure-input.service';
import {MeasureLocalService} from '../../../app/core/local-data-services/measure-local.service';
import {ObservationService} from '../../../app/core/observation.service';
import {PKLocalService} from '../../../app/core/local-data-services/pk-local.service';
import {Measure, MeasureContext, PK} from "../../../app/models/models";
import {OtherPkModalPage} from '../other-pk-modal/other-pk-modal';
import {ThemeProvider} from "../../../providers/theme/theme";

@Component({
  selector: 'action-buttons',
  templateUrl: 'action-buttons.html'
})
export class ActionButtons implements OnInit {
  buttonsType: string;
  measure: Measure;
  measureContext: MeasureContext;
  nextPkAvailable: boolean;
  previousPkAvailable: boolean;
  nextPkIsEndNumber: number;
  previousPk: PK;
  pk: PK;

  constructor(private alertCtrl: AlertController,
              public measureContextService: MeasureContextService,
              private measureInputService: MeasureInputService,
              private measureLocalService: MeasureLocalService,
              private modalCtrl: ModalController,
              private navCtrl: NavController,
              public observationService: ObservationService,
              private pkLocalService: PKLocalService,
              private themeProvider: ThemeProvider) {

    this.measure = MeasureInputService.getCurrentMeasure();
    this.measureContext = MeasureInputService.getMeasureContext();
    this.pk = MeasureInputService.getCurrentPk();
    this.buttonsType = this.updateButtonsType();
    this.previousPk = MeasureInputService.getPreviousPk();
  }

  ngOnInit() {
    if (Number(this.pk.number) % 5 != 0) {
      this.nextPkAvailable = false;
    } else {
      this.isNextPkAvailable().subscribe(
        (res: boolean) => {
          if (res) this.nextPkAvailable = res;
          else this.searchAvailableEndPk(this.pk.getNextMultipleOf5() - 1);
        });
    }

    this.isPreviousPkAvailable().subscribe(
      (res: boolean) => this.previousPkAvailable = res
    );

    this.measureInputService.currentMeasureUpdated$.subscribe(
      (measure: Measure) => this.measure = measure
    );
    this.measureInputService.currentPkUpdated$.subscribe(
      (pk: PK) => this.pk = pk
    );
    this.measureInputService.measureContextUpdated$.subscribe(
      (measureContext: MeasureContext) => {
        this.measureContext = measureContext;
        this.buttonsType = this.updateButtonsType();
      });
    this.measureInputService.previousPkUpdated$.subscribe(
      previousPk => this.previousPk = previousPk
    );
  }

  closeBT(): void {
    this.navCtrl.parent.select(1);
  }

  confirmLocation(): void {
    this.measureInputService.updateCurrentMeasure(this.measure);
    this.measureContextService.confirmLocation();
    this.isPreviousPkAvailable().subscribe(
      (res: boolean) => this.previousPkAvailable = res
    );
  }

  goToNextPK(): void {
    this.measureInputService.updatePreviousPk(this.pk);
    if (this.nextPkIsEndNumber) {
      this.updateLocation(this.pk.setNumber(this.nextPkIsEndNumber), false);
      this.nextPkIsEndNumber = null;
    } else {
      this.updateLocation(this.pk.setNumber(this.pk.getNextMultipleOf5()), false);
    }
  }

  goToPreviousPK(): void {
    this.measureInputService.updatePreviousPk(this.pk);
    this.updateLocation(this.pk.setNumber(this.pk.getPreviousMultipleOf5()), false);
  }

  goToSpecificPk(): void {
    this.measureInputService.updatePreviousPk(this.pk);
    this.updateLocation(this.pk, true, true);
  }

  registerMeasure(): void {
    this.measure.setNewDate();
    this.measureInputService.updateCurrentMeasure(this.measure);
    this.measureLocalService.put(this.measure).subscribe(
      () => {
        if (this.measure.isSpecific) {
          let previousPk = MeasureInputService.getPreviousPk();
          let message = 'PK:\t\t\t' + previousPk.number;
          message += '\nSens:\t\t' + (previousPk.way === 'ONE' ? 1 : 2);
          message += '\nPotentiel:\t' + (previousPk.potential === 'POSITIVE' ? 'positif' : 'négatif');

          let repositionAlert = this.alertCtrl.create({
            title: 'Attention',
            subTitle: 'Vous allez revenir à votre localisation initiale:',
            message: message,
            buttons: ['OK'],
            enableBackdropDismiss: false,
            cssClass: this.themeProvider.applicationTheme
          });

          repositionAlert.present();

          repositionAlert.onDidDismiss(
            (choice: string) => {
              this.updateLocation(previousPk);
              this.measureContextService.register();
            }
          );
        } else {
          this.measureContextService.register();
        }
      }
    );
  }

  showOtherPKModal(): void {
    let otherPkModal = this.modalCtrl.create(
      OtherPkModalPage,
      {},
      {
        cssClass: 'other-pk-modal',
        enableBackdropDismiss: false
      }
    );

    otherPkModal.onDidDismiss(
      (choice: string) => {
        if (choice === 'specific-pk') {
          this.goToSpecificPk();
        } else if (choice === 'reposition') {
          this.reposition();
        }
      }
    );

    otherPkModal.present();
  }

  undoNavigation() {
    this.measureContextService.undoNavigation();
    this.updateLocation(MeasureInputService.getPreviousPk());
    this.measureInputService.updatePreviousPk(null);
  }

  private isNextPkAvailable(): Observable<boolean> {
    let pk: PK = Object.assign(new PK(), this.pk);
    this.nextPkIsEndNumber = null;
    pk = pk.setNumber(pk.getNextMultipleOf5());
    return this.pkLocalService.findByNumberAndLocation(pk).map((pk: PK) => pk.hasCompleteLocation());
  }

  private isPreviousPkAvailable(): Observable<boolean> {
    let pk: PK = Object.assign(new PK(), this.pk);
    pk = pk.setNumber(pk.getPreviousMultipleOf5());
    return this.pkLocalService.findByNumberAndLocation(pk)
      .map((pk: PK) => pk.hasCompleteLocation());
  }

  private reposition(): void {
    this.measureInputService.updatePreviousPk(this.pk);
    this.updateLocation(this.pk, true, false);
  }

  private updateButtonsType(): string {
    if (this.measureContext.registered) {
      if (Number(this.pk.number) % 5 != 0) {
        this.nextPkAvailable = false;
      } else {
        this.isNextPkAvailable().subscribe(
          (res: boolean) => {
            if (res) this.nextPkAvailable = res;
            else this.searchAvailableEndPk(this.pk.getNextMultipleOf5() - 1);
          });
      }

      return 'navigation';
    } else if (this.measureContext.locationConfirmed === false) {
      return 'location';
    } else if (this.measureContext.locationConfirmed === true) {
      return 'measure';
    }
  }

  private updateLocation(pk: PK, isNewMeasure: boolean = null, isSpecific: boolean = false): void {
    this.pkLocalService.findByNumberAndLocation(pk)
      .flatMap((pk: PK) => {
        this.measureInputService.updateCurrentPk(pk);
        return this.measureLocalService.findByPk(pk);
      })
      .subscribe((measure: Measure) => {
        this.measure = measure;
        if (isSpecific !== null) {
          measure.isSpecific = isSpecific;
        }
        if (isNewMeasure || measure.isEmpty()) {
          this.measureContextService.prepareForNewMeasure();
        } else {
          if (Number(this.pk.number) % 5 != 0) {
            this.nextPkAvailable = false;
          } else {
            this.isNextPkAvailable().subscribe(
              (res: boolean) => {
                if (res) this.nextPkAvailable = res;
                else this.searchAvailableEndPk(this.pk.getNextMultipleOf5() - 1);
              });
          }
          this.isPreviousPkAvailable().subscribe(
            (res: boolean) => this.previousPkAvailable = res
          );
        }
        this.measureInputService.updateCurrentMeasure(this.measure);
      });
  }

  searchAvailableEndPk(pkNumber): boolean {
    if (Number(this.pk.number) > pkNumber) return false;

    this.isNextPkAvailableEnd(pkNumber).subscribe(
      (res: boolean) => {
        if (res) {
          this.nextPkAvailable = res;
          this.nextPkIsEndNumber = pkNumber;
          return;
        }
        else {
          this.searchAvailableEndPk(pkNumber - 1);
        }
      }
    );
  }

  private isNextPkAvailableEnd(number): Observable<boolean> {
    let pk: PK = Object.assign(new PK(), this.pk);
    pk = pk.setNumber(number);
    return this.pkLocalService.findByNumberAndLocation(pk).map((pk: PK) => pk.hasCompleteLocation());
  }

}



// WEBPACK FOOTER //
// ./src/pages/measure-input/action-buttons/action-buttons.ts